import type { Supplier } from '@/services';
import { supplierModel, supplierResource } from '@features/catalog';
import { supplierActions } from '@/actions';
import { failHard } from '@resource-kit/core';
import { registerRelationModel } from '@resource-kit/controllers';

/**
 * The relation-target barrel (#1804 batch C) — sibling of `./widgets.ts`, same
 * discipline: ONE explicit call from the composition root, never a bare
 * side-effect import whose timing the bundler decides.
 *
 * A model appears here when it is the target of some field's `relation`. That
 * is a strictly smaller set than "every resource": a target needs BOTH backend
 * halves, and only the catalog four have them —
 * `GET /api/catalog/<base>/by-ids` (#1793) and a non-empty `x-searchable`
 * (#1792). Only `supplier` has a field pointing at it today
 * (`ingredients.default_supplier_id`), so only `supplier` is registered; adding
 * a target that nothing references would be a capability claim with no caller.
 *
 * Every part of a registration is READ from the target's own declarations —
 * nothing is re-derived here. That is what makes the chain load-bearing end to
 * end: the v2 host declares `api.getMany` / `api.searchReference`, and this
 * barrel consumes those same two declarations directly. Restating the fetches
 * here instead would leave the host slots as declarations nobody reads — the
 * exact shape #1804 forbids.
 *
 * TESTS call this themselves when they render a definition carrying a
 * `relation` field. Hoisting the call into `src/test-setup.ts` to spare them
 * that was tried and MEASURED, and it does not work: setup runs before a
 * suite's `vi.mock` factories, so pulling this module's graph (`@/actions` →
 * every feature's resources and pages) in eagerly broke 88 of 283 test files
 * and took total setup from 28s to ~450s. The obligation stays with the suite.
 */
declare module '@resource-kit/core' {
  interface ModelRegistry {
    supplier: { record: Supplier };
  }
}

export function registerFeatureRelations(): void {
  const { getMany, searchReference } = supplierResource.host.api;
  // `ResourceAPI` makes both optional, because most models are not relation
  // targets. A target missing either is a resource whose reference reads
  // silently stopped existing, so it stops the composition root instead of
  // registering a source that cannot answer the questions asked of it.
  if (!getMany || !searchReference) {
    failHard(
      `${supplierModel.name}: a relation target must declare api.getMany and api.searchReference`,
    );
  }

  registerRelationModel('supplier', {
    getMany,
    searchReference,
    // Single-sourced from the model so a rename cannot desync the two.
    idField: supplierModel.idField,
    recName: supplierModel.recName,
    // Single-sourced from the action layer, which owns the route shape.
    detailPath: (id) => supplierActions.detailPath('main', id),
  });
}
